import type { PrototypeDesign } from '@aiai/shared';

type Preview = PrototypeDesign['preview'];

export const FONT_STACKS: Record<Preview['font'], string> = {
  sans: 'ui-sans-serif, system-ui, sans-serif',
  serif: "Georgia, 'Times New Roman', serif",
  mono: "ui-monospace, 'Cascadia Mono', Consolas, monospace",
  display: 'ui-sans-serif, system-ui, sans-serif',
};

/** Black or white, whichever reads on the given hex/rgb(a) accent. */
export function contrastText(color: string): string {
  const hex = color.match(/^#([0-9a-f]{6})/i)?.[1];
  if (!hex) return '#fff';
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum > 0.55 ? '#111' : '#fff';
}

/** The color at the given opacity — hex gets an alpha suffix, anything else color-mix. */
export function withAlpha(color: string, alpha: number): string {
  const a = Math.round(Math.max(0, Math.min(1, alpha)) * 255)
    .toString(16)
    .padStart(2, '0');
  if (/^#[0-9a-f]{6}$/i.test(color)) return `${color}${a}`;
  return `color-mix(in srgb, ${color} ${Math.round(alpha * 100)}%, transparent)`;
}

/**
 * Everything the full-page preview needs, derived from a design's handful of
 * preview tokens: muted ink and hairlines come from `text`, so dark styles
 * read as well as light ones without per-design extras.
 */
export function previewTokens(p: Preview) {
  return {
    ...p,
    fontFamily: FONT_STACKS[p.font],
    headingWeight: p.font === 'display' ? 800 : p.font === 'serif' ? 600 : 700,
    headingTracking: p.font === 'display' ? '-0.02em' : undefined,
    muted: withAlpha(p.text, 0.65),
    faint: withAlpha(p.text, 0.45),
    hairline: p.border ?? `1px solid ${withAlpha(p.text, 0.14)}`,
    divider: withAlpha(p.text, 0.12),
    subtle: withAlpha(p.text, 0.06),
    onAccent: contrastText(p.accent),
    accentSoft: withAlpha(p.accent, 0.16),
    /** Radius for small controls — capped so pill-ish styles stay usable. */
    controlRadius: Math.min(p.radius, 12),
  };
}

export type PreviewTokens = ReturnType<typeof previewTokens>;
